/**
 * usePricing() — headless hook for billing integration.
 *
 * The single required integration point for all products in the ecosystem.
 * Fetches catalog + subscription on mount, derives current product state
 * and upsell suggestions, and exposes checkout/upgrade/portal actions.
 *
 * @example
 * ```tsx
 * import { usePricing } from "@d-m-organization/pricing-ui";
 *
 * function UpgradeButton() {
 *   const { currentProduct, checkout, isLoading } = usePricing({ product: "qr" });
 *
 *   if (isLoading) return <Spinner />;
 *   if (currentProduct?.tier === "pro") return <span>Pro plan active</span>;
 *
 *   return (
 *     <button onClick={() => checkout({ kind: "product", product: "qr", tier: "pro", interval: "month" })
 *       .then(({ url }) => window.location.href = url)}>
 *       Upgrade to Pro
 *     </button>
 *   );
 * }
 * ```
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PricingError } from "./types.js";
import { usePricingContext } from "./PricingProvider.js";
import { deleteScheduledChange, fetchCatalog, fetchSubscription, postCheckout, postPortal, postUpgrade, postUpgradePreview, } from "./api.js";
import { computeUpsell } from "./upsell.js";
let _useAuth = null;
/**
 * Allow consumers to inject the auth hook at setup time.
 * This avoids a hard import dependency on @d-m-organization/hub-shared-lib.
 *
 * @example
 * ```ts
 * import { setAuthHook } from "@d-m-organization/pricing-ui";
 * import { useAuth } from "@d-m-organization/hub-shared-lib/auth/core";
 * setAuthHook(useAuth);
 * ```
 */
export function setAuthHook(hook) {
    _useAuth = hook;
}
function useAuthState() {
    if (_useAuth)
        return _useAuth();
    return { idToken: null, isAuthed: false, isAuthReady: true };
}
// ---------------------------------------------------------------------------
// Refresh detection
// ---------------------------------------------------------------------------
/**
 * Check if the current URL has ?refresh=true (set after Stripe checkout redirect).
 * If found, return true and clean the param from the URL via history.replaceState.
 */
function consumeRefreshParam() {
    if (typeof window === "undefined")
        return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get("refresh") !== "true")
        return false;
    // Clean the param from the URL without a navigation
    params.delete("refresh");
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
    window.history.replaceState(null, "", newUrl);
    return true;
}
// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function usePricing(opts) {
    const product = opts?.product;
    const { apiBase, catalogCache } = usePricingContext();
    const { idToken, isAuthed, isAuthReady } = useAuthState();
    const [catalog, setCatalog] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // HD-81: separate error state for previewUpgrade so the modal can render
    // a typed error inline without affecting the panel's main `error` field.
    const [previewError, setPreviewError] = useState(null);
    // Track whether we've consumed the refresh param on first mount
    const refreshConsumed = useRef(false);
    // Track the current AbortController so overlapping fetches can be cancelled
    const abortRef = useRef(null);
    // -------------------------------------------------------------------------
    // Fetch logic
    // -------------------------------------------------------------------------
    const loadData = useCallback(async (forceRefresh = false) => {
        // Abort any in-flight request to prevent stale responses overwriting newer ones
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setIsLoading(true);
        setError(null);
        try {
            // Catalog: check cache first
            let catalogData = forceRefresh
                ? undefined
                : catalogCache.get(apiBase);
            if (!catalogData) {
                catalogData = await fetchCatalog(apiBase);
                if (controller.signal.aborted)
                    return;
                catalogCache.set(apiBase, catalogData);
            }
            if (controller.signal.aborted)
                return;
            setCatalog(catalogData);
            // Subscription: only fetch if authenticated
            if (isAuthed && idToken) {
                const shouldRefresh = forceRefresh ||
                    (!refreshConsumed.current && consumeRefreshParam());
                refreshConsumed.current = true;
                const subData = await fetchSubscription(apiBase, idToken, shouldRefresh);
                if (controller.signal.aborted)
                    return;
                setSubscription(subData);
            }
            else {
                setSubscription(null);
            }
        }
        catch (err) {
            if (controller.signal.aborted)
                return;
            const pricingErr = err instanceof PricingError
                ? err
                : new PricingError(0, "unknown", String(err));
            setError(pricingErr);
        }
        finally {
            if (!controller.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [apiBase, catalogCache, idToken, isAuthed]);
    // -------------------------------------------------------------------------
    // Mount effect — fetch on mount and when auth state changes
    // -------------------------------------------------------------------------
    useEffect(() => {
        // Wait until auth has resolved (avoids a flash of unauthenticated state)
        if (!isAuthReady)
            return;
        loadData();
        // Cleanup: abort any in-flight request on unmount or dep change
        return () => {
            abortRef.current?.abort();
        };
    }, [isAuthReady, loadData]);
    // -------------------------------------------------------------------------
    // Derived state
    // -------------------------------------------------------------------------
    const currentProduct = useMemo(() => {
        if (!product)
            return null;
        if (!subscription)
            return null;
        const entitlement = subscription.entitlements[product];
        if (entitlement) {
            // HD-83: when entitlement is sourced from an individual subscription,
            // also surface the billing interval so the pricing panel can decide
            // between "Active" and "Switch to yearly/monthly" on the same tier.
            // Bundle-sourced entitlements fall through to the ecosystem record's
            // interval (also nullable for now, see EcosystemSubscription).
            let interval = null;
            if (entitlement.source === "individual") {
                const sub = subscription.products.find(p => p.productId === product);
                interval = sub?.interval ?? null;
            }
            else if (entitlement.source === "bundle") {
                interval = subscription.ecosystem?.interval ?? null;
            }
            // ADR-0008: surface the comp expiry only for admin grants. Non-comp
            // sources never carry one — omit the key entirely so ProductState stays
            // identical to its pre-comp shape for every paid/free/bundle user.
            return {
                tier: entitlement.tier,
                source: entitlement.source,
                interval,
                ...(entitlement.source === "admin"
                    ? { compExpiresAt: entitlement.expiresAt ?? null }
                    : {}),
            };
        }
        // Product not in entitlements → free tier
        return { tier: "free", source: "free", interval: null };
    }, [product, subscription]);
    const upsellSuggestion = useMemo(() => {
        if (!catalog || !subscription)
            return null;
        return computeUpsell(catalog, subscription);
    }, [catalog, subscription]);
    // -------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------
    function requireAuth() {
        if (!idToken) {
            throw new PricingError(401, "auth_required", "Authentication required. User must be signed in to perform this action.");
        }
        return idToken;
    }
    const checkout = useCallback(async (checkoutOpts) => {
        const token = requireAuth();
        return postCheckout(apiBase, token, checkoutOpts);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiBase, idToken]);
    const upgrade = useCallback(async (upgradeOpts) => {
        const token = requireAuth();
        return postUpgrade(apiBase, token, upgradeOpts);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiBase, idToken]);
    // HD-81: read-only Stripe upcoming-invoice for the proposed change.
    const previewUpgrade = useCallback(async (upgradeOpts) => {
        const token = requireAuth();
        try {
            const result = await postUpgradePreview(apiBase, token, upgradeOpts);
            setPreviewError(null);
            return result;
        }
        catch (err) {
            const pricingErr = err instanceof PricingError
                ? err
                : new PricingError(0, "unknown", String(err));
            setPreviewError(pricingErr);
            throw pricingErr;
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiBase, idToken]);
    const portalUrl = useCallback(async (portalOpts) => {
        const token = requireAuth();
        const result = await postPortal(apiBase, token, portalOpts);
        return result.portalUrl;
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiBase, idToken]);
    // HD-119: cancel a pending scheduled change, then refetch so the banner clears.
    const cancelScheduledChange = useCallback(async (product) => {
        const token = requireAuth();
        await deleteScheduledChange(apiBase, token, product);
        await loadData(true);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiBase, idToken, loadData]);
    const reportUsage = useCallback(async (_opts) => {
        throw new PricingError(501, "not_implemented", "Usage reporting is not available in v1. See HD-63 for roadmap.");
    }, []);
    const refetch = useCallback(async () => {
        catalogCache.invalidate(apiBase);
        await loadData(true);
    }, [apiBase, catalogCache, loadData]);
    // -------------------------------------------------------------------------
    // Return
    // -------------------------------------------------------------------------
    return {
        catalog,
        subscription,
        currentProduct,
        isLoading,
        error,
        checkout,
        upgrade,
        previewUpgrade,
        previewError,
        portalUrl,
        cancelScheduledChange,
        reportUsage,
        upsellSuggestion,
        refetch,
    };
}
